/**
 * 消息格式化服务
 * 负责将消息模板转换为 Markdown 格式
 */

import type { RecordData, FieldInfo } from "../types";

/**
 * 将消息模板中的字段变量替换为实际值
 * @param template 消息模板（如：门店：{门店名称}\n电话：{电话}）
 * @param record 记录数据
 * @param fields 字段列表（用于获取字段名称和ID的映射）
 * @returns 渲染后的消息内容
 */
export function renderMessageTemplate(
  template: string,
  record: RecordData,
  fields: FieldInfo[]
): string {
  let message = template;

  // 创建字段名称到ID的映射
  const fieldNameToId = new Map<string, string>();
  fields.forEach((field) => {
    fieldNameToId.set(field.name, field.id);
  });

  // 替换模板中的字段变量
  // 支持格式：{字段名称} 或 {字段ID}
  const variablePattern = /\{([^}]+)\}/g;

  message = message.replace(variablePattern, (match, fieldNameOrId) => {
    // 先尝试作为字段名称查找
    let fieldId = fieldNameToId.get(fieldNameOrId);

    // 如果没找到，尝试直接作为字段ID使用
    if (!fieldId) {
      fieldId = fieldNameOrId;
    }

    // 获取字段值
    const value = fieldId ? record.fields[fieldId] : undefined;

    // 处理不同类型的值
    if (value === null || value === undefined) {
      return "";
    }

    if (Array.isArray(value)) {
      return value.join(", ");
    }

    return String(value);
  });

  return message;
}

/**
 * 转换为 Markdown 格式
 * @param message 普通文本消息
 * @returns Markdown 格式的消息
 */
export function convertToMarkdown(message: string): string {
  // 保持换行符，每行后面加两个空格确保 Markdown 换行
  return message
    .split("\n")
    .map((line) => line.trimEnd() + "  ")
    .join("\n");
}

/**
 * 批量渲染消息
 * @param template 消息模板
 * @param records 记录列表
 * @param fields 字段列表
 * @returns 渲染后的消息列表（按记录ID映射）
 */
export function batchRenderMessages(
  template: string,
  records: RecordData[],
  fields: FieldInfo[]
): Map<string, string> {
  const messages = new Map<string, string>();

  for (const record of records) {
    const message = renderMessageTemplate(template, record, fields);
    messages.set(record.id, message);
  }

  return messages;
}
